// NIGHT-DIRECTSTAFF-2026-10-22 §3: "Match candidates" on a facility request -- Matt picks candidates by discipline and state
// and links them; the link shows on the candidate's timeline and the facility lead's. Manual only: nothing is sent.
//
// Stored with no new table: one activity_log row per link or unlink, type 'facility_match', on the CANDIDATE
// (candidate_id), the facility lead by id in the note -- the same "lead_id=<uuid>" convention as the email rows, which is how
// the lead's timeline finds them:
//   "Matched to facility request <lead8> · <facility> · by <staff> · lead_id=<uuid>"
//   "Unmatched from facility request <lead8> · <facility> · by <staff> · lead_id=<uuid>"
// Who is linked now = each candidate's latest row for that lead (matched, unless a later unmatch). Nothing is deleted.
export const MATCH_LOG_TYPE = 'facility_match';
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const oneLine = (v) => s(v).replace(/[\r\n·]+/g, ' ').replace(/\s+/g, ' ').trim();
const isTest = (c) => !!c && Array.isArray(c.tags) && c.tags.some((x) => s(x).toLowerCase() === 'test');

export function matchNote({ linked, leadId, facility, by = '' }) {
  if (!UUID.test(s(leadId))) throw new Error('A facility request id is needed.');
  return `${linked ? 'Matched to' : 'Unmatched from'} facility request ${s(leadId).slice(0, 8)} · ${oneLine(facility) || 'facility'}${s(by) ? ` · by ${oneLine(by)}` : ''} · lead_id=${s(leadId)}`;
}
const RE = /^(Matched to|Unmatched from) facility request ([0-9a-f]{8}) · (.+?)(?: · by (.+?))? · lead_id=([0-9a-f-]{36})$/i;
/** -> { linked, lead8, facility, by, leadId } | null */
export function parseMatchNote(note) {
  const m = RE.exec(s(note));
  return m ? { linked: /^Matched/i.test(m[1]), lead8: m[2], facility: m[3], by: m[4] || '', leadId: m[5] } : null;
}

/** activity rows -> the candidate ids linked to this lead now (each candidate's latest row wins). */
export function linkedCandidates(rows = [], leadId) {
  const last = new Map();
  const sorted = [...rows].filter((r) => s(r.type) === MATCH_LOG_TYPE && r.candidate_id !== null && r.candidate_id !== undefined)
    .sort((a, b) => s(a.created_at).localeCompare(s(b.created_at)) || (Number(a.id) || 0) - (Number(b.id) || 0));
  for (const r of sorted) {
    const m = parseMatchNote(r.note);
    if (!m || m.leadId !== s(leadId)) continue;
    last.set(String(r.candidate_id), m.linked);
  }
  return new Set([...last].filter(([, on]) => on).map(([id]) => id));
}

const norm = (v) => s(v).toUpperCase();
/** The candidates to offer: same discipline and state when given (blank = any), test candidates left out, linked first. */
export function candidateChoices(candidates = [], { discipline = '', state = '' } = {}, linked = new Set()) {
  const d = norm(discipline), st = norm(state);
  return candidates
    .filter((c) => !isTest(c))
    .filter((c) => !d || norm(c.specialty) === d)
    .filter((c) => !st || norm(c.state) === st || (Array.isArray(c.licenses) && c.licenses.map(norm).includes(st)) || norm(c.licenses).split(/[\s,]+/).includes(st))
    .sort((a, b) => Number(linked.has(String(b.id))) - Number(linked.has(String(a.id))) || s(a.name).localeCompare(s(b.name)));
}

/** "Synthetic City, CO" -> "CO"; "CO" -> "CO"; else ''. */
export const stateOf = (cityState) => { const m = /(?:^|,\s*)([A-Za-z]{2})\s*$/.exec(s(cityState)); return m ? m[1].toUpperCase() : ''; };

export function makeMatchApi({ supabase }) {
  const whoAmI = async () => { try { const { data } = await supabase.auth.getUser(); return (data && data.user && data.user.email) || ''; } catch { return ''; } };
  return {
    /** -> activity rows of this lead's matches */
    async list(leadId) {
      const { data, error } = await supabase.from('activity_log').select('id,candidate_id,type,note,created_at').eq('type', MATCH_LOG_TYPE).like('note', `%lead_id=${s(leadId)}`);
      if (error) throw new Error(error.message || 'Matches could not be read.');
      return data || [];
    },
    async set(lead, facility, candidateId, linked) {
      const by = await whoAmI();
      const { error } = await supabase.from('activity_log').insert({ candidate_id: candidateId, date: new Date().toISOString().slice(0, 10), type: MATCH_LOG_TYPE,
        note: matchNote({ linked, leadId: lead.id, facility, by }) });
      if (error) throw new Error(error.message || 'Not saved.');
      return true;
    },
  };
}
